var _hooksNewsletterQuery;
import { useCallback, useEffect, useState } from "react";
import { fetchQuery, graphql } from "react-relay";
import useSWRImmutable from "swr/immutable";
import { useSdkContext } from "~/components/contexts";
import { useFallbackStorageValue } from "~/lib/hooks";
import { addMinutes, notEmpty, unionArray } from "~/lib/utils";
import { fetchUserContext, signUpNewsletters } from "./apis";
import { SignedUpLastSyncKey, SignedUpNewsletterSwrFetchKey } from "./consts";
import { SignUpMatrixV1LocalStorageKey } from "./sign-up-newsletter/social-buttons/consts";
const query = _hooksNewsletterQuery !== void 0 ? _hooksNewsletterQuery : (_hooksNewsletterQuery = require("../../../queries/__generated__/hooksNewsletterQuery.graphql"), _hooksNewsletterQuery.hash && _hooksNewsletterQuery.hash !== "07926024ff85778e6f495ad0f59ef656" && console.error("The definition of 'hooksNewsletterQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _hooksNewsletterQuery);
export const useNewsletterApi = () => {
  const {
    environment,
    handleSetV1SignedUpNewslettersLocalStorage,
    user,
    v1SignedUpNewsletters
  } = useSdkContext();
  if (!environment) console.error("Relay environment not found, make sure you wrapped your application with RelayEnvironmentProvider");
  const {
    set: setLastSync,
    value: lastSync
  } = useFallbackStorageValue(SignedUpLastSyncKey, {
    defaultValue: 0
  });
  const {
    remove: removeDataMatrixInLocalStorage,
    value: dataMatrixInLocalStorage
  } = useFallbackStorageValue(SignUpMatrixV1LocalStorageKey, {
    defaultValue: {}
  });
  const handleSetLocalStorage = useCallback(data => {
    // No need to set the local storage if user is not login
    user && setLastSync(Date.now());
    handleSetV1SignedUpNewslettersLocalStorage(data);
  }, [handleSetV1SignedUpNewslettersLocalStorage, setLastSync, user]);
  const getIsDataOutDated = () => Date.now() > addMinutes(lastSync !== null && lastSync !== void 0 ? lastSync : 0, 60).getTime();
  const {
    data,
    mutate
  } = useSWRImmutable(user ? SignedUpNewsletterSwrFetchKey : null, fetchUserContext(environment), {
    fallbackData: v1SignedUpNewsletters,
    isPaused: () => !getIsDataOutDated(),
    onSuccess: data => handleSetLocalStorage(data),
    refreshInterval: 1000 * 30,
    revalidateOnMount: true
  });
  const [isDataReady, setIsDataReady] = useState(false);
  useEffect(() => {
    if (data) setIsDataReady(true);
  }, [data]);
  const fetchNewsletterByIds = useCallback(async (newsletterIds, first) => {
    const response = await fetchQuery(environment, query, {
      first,
      newsletterIds
    }).toPromise();
    return response === null || response === void 0 ? void 0 : response.newsletters.edges.map(({
      node
    }) => {
      var _node$description, _node$homepageDescrip, _node$summary;
      return {
        alternativeName: node.alternativeName,
        description: (_node$description = node.description) !== null && _node$description !== void 0 && _node$description.text ? {
          text: node.description.text
        } : undefined,
        entityId: node.entityId,
        homepageDescription: (_node$homepageDescrip = node.homepageDescription) !== null && _node$homepageDescrip !== void 0 && _node$homepageDescrip.text ? {
          text: node.homepageDescription.text
        } : undefined,
        name: node.name,
        summary: (_node$summary = node.summary) !== null && _node$summary !== void 0 && _node$summary.text ? {
          text: node.summary.text
        } : undefined
      };
    });
  }, [environment]);
  const handleSignUpNewsletter = useCallback(async (newsletterItemIds, email, dataMatrix) => {
    // If dataMatrix in context is empty, will use local storage one
    const isUseLocalStorageDataMatrix = !notEmpty(dataMatrix);
    const response = await signUpNewsletters(environment, newsletterItemIds, email, isUseLocalStorageDataMatrix ? dataMatrixInLocalStorage : dataMatrix);
    if (notEmpty(response.errors)) return response;
    const mergedArray = unionArray(data !== null && data !== void 0 ? data : [], newsletterItemIds, v1SignedUpNewsletters);
    await mutate(mergedArray, {
      optimisticData: mergedArray,
      populateCache: true,
      revalidate: false,
      rollbackOnError: true
    });

    // Clear the local storage if dataMatrix has been used
    isUseLocalStorageDataMatrix && removeDataMatrixInLocalStorage();
    handleSetLocalStorage(mergedArray);
    return response;
  }, [data, dataMatrixInLocalStorage, environment, handleSetLocalStorage, mutate, removeDataMatrixInLocalStorage, v1SignedUpNewsletters]);
  return {
    data,
    fetchNewsletterByIds,
    handleSignUpNewsletter,
    isDataReady,
    mutate
  };
};
export const useSignedUpNewsletter = () => {
  const {
    data
  } = useNewsletterApi();
  const {
    v1SignedUpNewsletters
  } = useSdkContext();
  const [signedUpNewsletterItemIds, setSignedUpNewsletterItemIds] = useState(unionArray(data !== null && data !== void 0 ? data : [], v1SignedUpNewsletters !== null && v1SignedUpNewsletters !== void 0 ? v1SignedUpNewsletters : []));

  // Preventing changes on transition by delaying the set of signed up newsletter
  useEffect(() => {
    const timer = setTimeout(() => setSignedUpNewsletterItemIds(unionArray(data !== null && data !== void 0 ? data : [], v1SignedUpNewsletters !== null && v1SignedUpNewsletters !== void 0 ? v1SignedUpNewsletters : [])), 1500);
    return () => clearTimeout(timer);
  }, [data, v1SignedUpNewsletters]);
  return signedUpNewsletterItemIds;
};